import type { OcrInProgress } from '@/services/ingestion'

/**
 * What the record says about a scan that outran the one-request OCR budget.
 *
 * The server marks `parse_report.ocr_in_progress` while the remaining pages are
 * read behind the response, and leaves it as `incomplete` when they could not
 * be (the engine's 40-page cap, or a read that failed). Both are words a person
 * must be able to read on the record and on the attachment's row: an attachment
 * detail that says "re-parse to continue" is stale the moment the background
 * pass is running, and a silent half-read scan is the failure this exists to end.
 */

export type OcrNoteTone = 'running' | 'incomplete'

export interface OcrNote {
  tone: OcrNoteTone
  text: string
}

function pages(count: number): string {
  return `${count} page${count === 1 ? '' : 's'}`
}

/** True while the background pass has not finished (queued or running). */
export function ocrStillRunning(state: OcrInProgress | null | undefined): boolean {
  return state?.status === 'queued' || state?.status === 'running'
}

/** The record-level note, or null when every page was read. */
export function ocrRecordNote(state: OcrInProgress | null | undefined): OcrNote | null {
  if (!state) return null
  const tone: OcrNoteTone = state.status === 'incomplete' ? 'incomplete' : 'running'
  const fallback = tone === 'incomplete'
    ? `OCR incomplete: ${pages(state.pagesRemaining)} not read — confirm by eye`
    : `OCR in progress: ${pages(state.pagesRemaining)} remaining`
  return { tone, text: state.note || fallback }
}

/** The note for one attachment, matched by the stored original's sha-256 (else by
 *  file name), or null when that attachment is not owed pages. */
export function ocrAttachmentNote(
  state: OcrInProgress | null | undefined,
  attachment: { sha256?: string | null; filename?: string | null },
): OcrNote | null {
  if (!state) return null
  const entry = (state.attachments ?? []).find((a) =>
    (attachment.sha256 && a.sha256 === attachment.sha256)
    || (!a.sha256 && attachment.filename && a.filename === attachment.filename))
  if (!entry) return null
  return state.status === 'incomplete'
    ? { tone: 'incomplete', text: `OCR incomplete: ${pages(entry.pagesRemaining)} not read — confirm by eye` }
    : { tone: 'running', text: `OCR in progress: ${pages(entry.pagesRemaining)} remaining` }
}
